export const DEFAULT_OVERVIEW_STATE = {
  refNo: "",
  title: "",
  standardOwnerId: "",
  standardOwnerLabel: "",
  responsibleTeam: "",
  functionalOwner: "",
  shortDescription: "",
  category: "",
  documentType: "",
  issueNumber: "",
  level: "",
  status: "",
  nextReleaseStatus: "",
  authorisedVariations: "",
  nrnc: "",
  traderInStock: false,
  publicationDate: "",
  complianceDate: "",
  reviewDate: "",
  withdrawalDate: "",
  reasonForChange: "",
  sensitivity: "",
  visibility: "",
  accessGroup: [],
  tags: [],
  attachments: [],
};

export const DEFAULT_OTHER_EDITIONS_STATE = {
  documents: [],
  selectedIds: [],
};

export const DEFAULT_PDSS_STATE = {
  pdssApplicable: "",
  pdssAllProjects: "",
  level1: "",
  level2: "",
  level3: "",
  level4: "",
};

export const DEFAULT_REFERENCE_STATE = {
  refNo: "",
  linksIn: [],
  linksOut: [],
  modulesIn: [],
  modulesOut: [],
};

export const PAGE_MODE_COPY_ISSUE = "copy-issue";

export const formatDateInput = (value) => {
  if (!value) return "";
  const text = String(value);
  if (text.length >= 10 && text.includes("T")) return text.slice(0, 10);
  return text;
};

export const toNumberOrNull = (value) => {
  if (value == null || value === "") return null;
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
};

export const isDeletedStatusValue = (value) => {
  const raw = String(value ?? "").trim().toLowerCase();
  return raw === "d" || raw === "delete" || raw === "deleted";
};

export const isDeletedSummary = (summary) =>
  isDeletedStatusValue(summary?.statusId) || isDeletedStatusValue(summary?.status);

export const resolveCreatedStandardId = (response, sourceStandardId = null) => {
  const sourceId = String(sourceStandardId ?? "").trim();
  const candidates = [
    response?.summary?.standardId,
    response?.summary?.id,
    response?.standardId,
    response?.id,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  const uniqueCandidates = [...new Set(candidates)];
  const createdId = uniqueCandidates.find((value) => value !== sourceId);
  return createdId || uniqueCandidates[0] || "";
};

export const resolveCopiedIssueNumber = (sourceIssue, copiedIssue) => {
  const source = String(sourceIssue ?? "").trim();
  const copied = String(copiedIssue ?? "").trim();

  if (copied && copied !== source) return copied;
  if (/^\d+$/.test(source)) return String(Number(source) + 1);
  return copied || source;
};

const normalizeOwnerText = (value) => String(value ?? "").trim();

export const formatStandardOwnerLabel = (owner) => {
  if (!owner) return "";

  const label = normalizeOwnerText(
    owner?.standardOwnerLabel ??
      owner?.standardOwner ??
      owner?.label ??
      owner?.value
  );
  const name = normalizeOwnerText(
    owner?.standardOwnerName ??
      owner?.standardOwner ??
      owner?.name ??
      owner?.displayName ??
      owner?.fullName ??
      owner?.ownerName
  );
  const email = normalizeOwnerText(
    owner?.standardOwnerEmail ??
      owner?.standardOwnerMail ??
      owner?.standardOwnerUserPrincipalName ??
      owner?.email ??
      owner?.mail ??
      owner?.userPrincipalName
  );

  if (name && email) return `${name} (${email})`;
  if (label) return label;
  if (name) return name;
  if (email) return email;
  return "";
};

const buildTagKey = (id, value) => {
  const normalizedId = String(id ?? "").trim();
  if (normalizedId) return `id:${normalizedId}`;

  const normalizedValue = String(value ?? "").trim().toLowerCase();
  if (normalizedValue) return `value:${normalizedValue}`;

  return "";
};

const dedupeTags = (items = []) => {
  const seen = new Set();

  return items.filter((item) => {
    const key = buildTagKey(item?.id, item?.value);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const resolveStandardOwnerId = (source) =>
  source?.standardOwnerId != null ? String(source.standardOwnerId) : "";

const resolveStandardOwnerLabel = (summary, fallbackSource = null) => {
  const directLabel = formatStandardOwnerLabel(summary);
  if (directLabel) return directLabel;

  if (!fallbackSource) return "";

  const summaryOwnerId = resolveStandardOwnerId(summary);
  const fallbackOwnerId = resolveStandardOwnerId(fallbackSource);
  if (!summaryOwnerId || !fallbackOwnerId || summaryOwnerId !== fallbackOwnerId) {
    return "";
  }

  return formatStandardOwnerLabel(fallbackSource);
};

export const buildTagStateFromSummary = (summary) => {
  if (Array.isArray(summary?.tags) && summary.tags.length) {
    return dedupeTags(
      summary.tags
        .map((tag) => {
          if (tag == null) return null;
          if (typeof tag === "object") {
            const id = String(tag.id ?? tag.tagId ?? "").trim();
            const value = String(
              tag.value ?? tag.label ?? tag.name ?? tag.title ?? ""
            ).trim();
            if (!id && !value) return null;
            return { id, value };
          }
          const raw = String(tag).trim();
          return raw ? { id: raw, value: raw } : null;
        })
        .filter(Boolean)
    );
  }

  if (Array.isArray(summary?.tagIds)) {
    return dedupeTags(summary.tagIds.map((id) => ({ id: String(id), value: "" })));
  }

  return [];
};

export const buildTagIdsFromSummary = (summary) =>
  normalizeIdArray(buildTagStateFromSummary(summary).map((tag) => tag?.id));

const normalizeRequiredString = (value) => String(value ?? "").trim();
const normalizeNullableString = (value) => {
  const next = String(value ?? "").trim();
  return next || null;
};
const normalizeReferenceNumber = (value, fallback = "") => {
  const next = String(value ?? "").trim();
  return next || fallback;
};

// Quill initialises empty content as "<p><br></p>" which is semantically
// identical to null/"". Treat these as null to avoid spurious dirty state.
const normalizeRichText = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw || raw === "<p><br></p>" || raw === "<p></p>") return null;
  return raw;
};

const buildAclRole = (visibility) => {
  if (visibility === "admin") {
    return { allowInternal: false, allowExternal: false };
  }
  if (visibility === "internal") {
    return { allowInternal: true, allowExternal: false };
  }
  if (visibility === "external") {
    return { allowInternal: false, allowExternal: true };
  }
  if (visibility === "internal-external") {
    return { allowInternal: true, allowExternal: true };
  }
  return null;
};

const buildVisibilityFromAclRole = (role) => {
  const hasExplicitRole =
    role &&
    (role.allowInternal != null || role.allowExternal != null);

  if (!hasExplicitRole) return "";

  const allowInternal = Boolean(role.allowInternal);
  const allowExternal = Boolean(role.allowExternal);

  if (allowInternal) {
    return allowExternal ? "internal-external" : "internal";
  }

  if (allowExternal) return "external";
  return "admin";
};

const normalizeIdArray = (items) =>
  (items ?? [])
    .map((item) => toNumberOrNull(item))
    .filter((item) => item != null)
    .sort((a, b) => a - b);

const normalizeLinkArray = (items) =>
  Array.from(
    (items ?? [])
      .reduce((map, item) => {
        const standardId = toNumberOrNull(item?.standardId);
        const linkType = normalizeRequiredString(item?.linkType || "Related") || "Related";
        if (standardId == null) return map;
        const key = `${standardId}:${linkType}`;
        if (!map.has(key)) map.set(key, { standardId, linkType });
        return map;
      }, new Map())
      .values(),
  ).sort((a, b) =>
    a.standardId !== b.standardId
      ? a.standardId - b.standardId
      : a.linkType.localeCompare(b.linkType),
  );

const sameArrayValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const sameStateValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const buildCurrentAclRole = (visibility) => buildAclRole(visibility);

const buildOriginalAclRole = (summary) =>
  buildAclRole(buildVisibilityFromAclRole(summary?.acl?.role));

const normalizeAccessGroups = (items) =>
  (items ?? [])
    .map((item) => {
      const id = toNumberOrNull(
        item?.groupId ?? item?.id ?? item?.value ?? item,
      );
      if (id == null) return null;

      const name = String(
        item?.name ?? item?.groupName ?? item?.label ?? id,
      ).trim();

      return {
        id: String(id),
        name: name || String(id),
      };
    })
    .filter(Boolean)
    .sort((left, right) => Number(left.id) - Number(right.id));

const normalizeAccessGroupIds = (items) =>
  normalizeIdArray(
    (items ?? []).map((item) => item?.groupId ?? item?.id ?? item?.value ?? item),
  );

const buildAccessGroupsFromSummary = (summary) =>
  normalizeAccessGroups(summary?.acl?.groups ?? summary?.acl?.groupIds ?? []);

const buildCurrentPdss = (state) => ({
  pdssApplicable: toNumberOrNull(state?.pdssApplicable),
  pdssAllProjects: toNumberOrNull(state?.pdssAllProjects),
  level1: toNumberOrNull(state?.level1),
  level2: toNumberOrNull(state?.level2),
  level3: toNumberOrNull(state?.level3),
  level4: toNumberOrNull(state?.level4),
});

const buildOriginalPdss = (summary) => ({
  pdssApplicable: toNumberOrNull(summary?.pdss?.pdssApplicable),
  pdssAllProjects: toNumberOrNull(summary?.pdss?.pdssAllProjects),
  level1: toNumberOrNull(summary?.pdss?.level1),
  level2: toNumberOrNull(summary?.pdss?.level2),
  level3: toNumberOrNull(summary?.pdss?.level3),
  level4: toNumberOrNull(summary?.pdss?.level4),
});

const buildSupersededIdsFromDocuments = (documents) =>
  normalizeIdArray((documents ?? []).map((doc) => doc?.standardId));

const buildReferenceLinkItem = (item) => {
  const standardId = toNumberOrNull(item?.standardId ?? item?.id);
  if (standardId == null) return null;

  const linkType =
    normalizeRequiredString(item?.linkType || "Related") || "Related";

  return {
    standardId,
    referenceNumber: normalizeReferenceNumber(
      item?.referenceNumber ??
        item?.reference ??
        item?.refNo ??
        item?.ref ??
        item?.summary?.referenceNumber,
      String(standardId),
    ),
    linkType,
  };
};

const buildReferenceModuleItem = (item) => {
  const standardId = toNumberOrNull(
    typeof item === "object" && item !== null
      ? item?.standardId ?? item?.id
      : item,
  );
  if (standardId == null) return null;

  return {
    standardId,
    referenceNumber: normalizeReferenceNumber(
      typeof item === "object" && item !== null
        ? item?.referenceNumber ??
            item?.reference ??
            item?.refNo ??
            item?.ref ??
            item?.summary?.referenceNumber
        : null,
      String(standardId),
    ),
  };
};

const buildOtherEditionDocumentItem = (item) => {
  const source =
    typeof item === "object" && item !== null
      ? item
      : null;
  const standardId = toNumberOrNull(
    source ? source?.standardId ?? source?.id : item,
  );
  if (standardId == null) return null;

  return {
    id: normalizeRequiredString(source?.id) || String(standardId),
    standardId,
    refNo: normalizeReferenceNumber(
      source?.refNo ??
        source?.referenceNumber ??
        source?.reference ??
        source?.ref ??
        source?.summary?.referenceNumber,
      String(standardId),
    ),
    title: normalizeRequiredString(
      source?.title ?? source?.summary?.title,
    ),
    issue: normalizeRequiredString(
      source?.issue ?? source?.summary?.issue,
    ),
    status: normalizeRequiredString(
      source?.status ?? source?.summary?.status,
    ),
  };
};

const resolveSupersededDocumentItems = (summary) => {
  const candidates = [
    summary?.supersededDocuments,
    summary?.supersededStandards,
    summary?.supersededStandardIds,
  ];

  return (
    candidates.find((items) => Array.isArray(items) && items.length > 0) ??
    (Array.isArray(summary?.supersededStandardIds)
      ? summary.supersededStandardIds
      : [])
  );
};

const buildUniqueReferenceLinks = (items = []) =>
  Array.from(
    items.reduce((map, item) => {
      const nextItem = buildReferenceLinkItem(item);
      if (!nextItem) return map;
      map.set(`${nextItem.standardId}:${nextItem.linkType}`, nextItem);
      return map;
    }, new Map()).values(),
  );

const buildUniqueReferenceModules = (items = []) =>
  Array.from(
    items.reduce((map, item) => {
      const nextItem = buildReferenceModuleItem(item);
      if (!nextItem) return map;
      map.set(String(nextItem.standardId), nextItem);
      return map;
    }, new Map()).values(),
  );

export const buildOtherEditionsState = (summary) => ({
  documents: Array.from(
    resolveSupersededDocumentItems(summary).reduce((map, item) => {
      const nextItem = buildOtherEditionDocumentItem(item);
      if (!nextItem) return map;
      map.set(String(nextItem.standardId), nextItem);
      return map;
    }, new Map()).values(),
  ),
  selectedIds: [],
});

const normalizeStandardIdKey = (value) => String(value ?? "").trim();

const hasResolvedReferenceLabel = (value, standardId) => {
  const normalizedValue = normalizeRequiredString(value);
  const normalizedStandardId = normalizeStandardIdKey(standardId);
  return Boolean(normalizedValue) && normalizedValue !== normalizedStandardId;
};

const mergeResolvedReferenceLabel = (nextValue, previousValue, standardId) => {
  if (hasResolvedReferenceLabel(nextValue, standardId)) {
    return nextValue;
  }

  if (hasResolvedReferenceLabel(previousValue, standardId)) {
    return previousValue;
  }

  return nextValue ?? previousValue ?? "";
};

const buildReferenceLinkKey = (item) => {
  const standardId = normalizeStandardIdKey(item?.standardId);
  if (!standardId) return "";

  const linkType =
    normalizeRequiredString(item?.linkType || "Related") || "Related";

  return `${standardId}:${linkType}`;
};

const buildStandardItemKey = (item) =>
  normalizeStandardIdKey(item?.standardId ?? item?.id);

const mergeReferenceEntries = (
  nextItems = [],
  previousItems = [],
  buildKey,
  labelField,
) => {
  const previousItemMap = (previousItems ?? []).reduce((map, item) => {
    const key = buildKey(item);
    if (!key) return map;
    map.set(key, item);
    return map;
  }, new Map());

  return (nextItems ?? []).map((nextItem) => {
    const key = buildKey(nextItem);
    if (!key) return nextItem;

    const previousItem = previousItemMap.get(key);
    if (!previousItem) return nextItem;

    const standardId = nextItem?.standardId ?? previousItem?.standardId;

    return {
      ...previousItem,
      ...nextItem,
      [labelField]: mergeResolvedReferenceLabel(
        nextItem?.[labelField],
        previousItem?.[labelField],
        standardId,
      ),
    };
  });
};

export const mergeReferenceStateWithResolvedNumbers = (
  nextState,
  previousState = DEFAULT_REFERENCE_STATE,
) => ({
  ...DEFAULT_REFERENCE_STATE,
  ...(nextState ?? {}),
  linksIn: mergeReferenceEntries(
    nextState?.linksIn,
    previousState?.linksIn,
    buildReferenceLinkKey,
    "referenceNumber",
  ),
  linksOut: mergeReferenceEntries(
    nextState?.linksOut,
    previousState?.linksOut,
    buildReferenceLinkKey,
    "referenceNumber",
  ),
  modulesIn: mergeReferenceEntries(
    nextState?.modulesIn,
    previousState?.modulesIn,
    buildStandardItemKey,
    "referenceNumber",
  ),
  modulesOut: mergeReferenceEntries(
    nextState?.modulesOut,
    previousState?.modulesOut,
    buildStandardItemKey,
    "referenceNumber",
  ),
});

export const mergeOtherEditionsStateWithResolvedNumbers = (
  nextState,
  previousState = DEFAULT_OTHER_EDITIONS_STATE,
) => {
  const previousDocumentMap = (previousState?.documents ?? []).reduce((map, item) => {
    const key = buildStandardItemKey(item);
    if (!key) return map;
    map.set(key, item);
    return map;
  }, new Map());

  const mergedDocuments = (nextState?.documents ?? []).map((nextItem) => {
    const key = buildStandardItemKey(nextItem);
    const previousItem = key ? previousDocumentMap.get(key) : null;

    if (!previousItem) {
      return {
        ...nextItem,
        id:
          normalizeRequiredString(nextItem?.id) ||
          normalizeStandardIdKey(nextItem?.standardId),
      };
    }

    const standardId = nextItem?.standardId ?? previousItem?.standardId;

    return {
      ...previousItem,
      ...nextItem,
      id:
        normalizeRequiredString(nextItem?.id) ||
        normalizeRequiredString(previousItem?.id) ||
        normalizeStandardIdKey(standardId),
      refNo: mergeResolvedReferenceLabel(
        nextItem?.refNo,
        previousItem?.refNo,
        standardId,
      ),
      title:
        normalizeRequiredString(nextItem?.title) ||
        normalizeRequiredString(previousItem?.title),
      issue:
        normalizeRequiredString(nextItem?.issue) ||
        normalizeRequiredString(previousItem?.issue),
      status:
        normalizeRequiredString(nextItem?.status) ||
        normalizeRequiredString(previousItem?.status),
    };
  });

  const availableIds = new Set(
    mergedDocuments
      .map((item) => normalizeRequiredString(item?.id))
      .filter(Boolean),
  );
  const nextSelectedIds = Array.isArray(nextState?.selectedIds)
    ? nextState.selectedIds
    : [];
  const previousSelectedIds = Array.isArray(previousState?.selectedIds)
    ? previousState.selectedIds
    : [];
  const selectedIds = (nextSelectedIds.length
    ? nextSelectedIds
    : previousSelectedIds
  )
    .map((item) => String(item))
    .filter((item) => availableIds.has(item));

  return {
    ...DEFAULT_OTHER_EDITIONS_STATE,
    ...(nextState ?? {}),
    documents: mergedDocuments,
    selectedIds,
  };
};

export const mergeEditStatesWithResolvedNumbers = (
  nextStates,
  previousStates = null,
) => {
  if (!previousStates) return nextStates;

  return {
    ...nextStates,
    otherEditionsState: mergeOtherEditionsStateWithResolvedNumbers(
      nextStates?.otherEditionsState,
      previousStates?.otherEditionsState,
    ),
    referenceState: mergeReferenceStateWithResolvedNumbers(
      nextStates?.referenceState,
      previousStates?.referenceState,
    ),
  };
};

const readResolvedReferenceNumber = (resolvedMap, standardId) => {
  if (!(resolvedMap instanceof Map)) return "";

  const numericStandardId = toNumberOrNull(standardId);
  if (numericStandardId != null && resolvedMap.has(numericStandardId)) {
    return normalizeRequiredString(resolvedMap.get(numericStandardId));
  }

  const standardIdKey = normalizeStandardIdKey(standardId);
  if (standardIdKey && resolvedMap.has(standardIdKey)) {
    return normalizeRequiredString(resolvedMap.get(standardIdKey));
  }

  return "";
};

const collectUnresolvedStandardIds = (items = [], labelField, collection) => {
  (items ?? []).forEach((item) => {
    const standardId = toNumberOrNull(item?.standardId ?? item?.id);
    if (standardId == null) return;

    const currentLabel = item?.[labelField];
    if (hasResolvedReferenceLabel(currentLabel, standardId)) return;
    collection.add(standardId);
  });
};

const applyResolvedLabelsToItems = (items = [], labelField, resolvedMap) => {
  let changed = false;

  const nextItems = (items ?? []).map((item) => {
    const resolvedReferenceNumber = readResolvedReferenceNumber(
      resolvedMap,
      item?.standardId ?? item?.id,
    );
    if (
      !resolvedReferenceNumber ||
      normalizeRequiredString(item?.[labelField]) === resolvedReferenceNumber
    ) {
      return item;
    }

    changed = true;
    return {
      ...item,
      [labelField]: resolvedReferenceNumber,
    };
  });

  return changed ? nextItems : items;
};

export const collectEditStateUnresolvedReferenceIds = ({
  otherEditionsState = DEFAULT_OTHER_EDITIONS_STATE,
  referenceState = DEFAULT_REFERENCE_STATE,
} = {}) => {
  const unresolvedIds = new Set();

  collectUnresolvedStandardIds(referenceState?.linksIn, "referenceNumber", unresolvedIds);
  collectUnresolvedStandardIds(referenceState?.linksOut, "referenceNumber", unresolvedIds);
  collectUnresolvedStandardIds(referenceState?.modulesIn, "referenceNumber", unresolvedIds);
  collectUnresolvedStandardIds(referenceState?.modulesOut, "referenceNumber", unresolvedIds);
  collectUnresolvedStandardIds(otherEditionsState?.documents, "refNo", unresolvedIds);

  return [...unresolvedIds];
};

export const applyResolvedReferenceNumbersToEditStates = (
  currentStates,
  resolvedMap,
) => {
  if (!currentStates || !(resolvedMap instanceof Map) || !resolvedMap.size) {
    return currentStates;
  }

  const nextReferenceState = {
    ...DEFAULT_REFERENCE_STATE,
    ...(currentStates.referenceState ?? {}),
    linksIn: applyResolvedLabelsToItems(
      currentStates.referenceState?.linksIn,
      "referenceNumber",
      resolvedMap,
    ),
    linksOut: applyResolvedLabelsToItems(
      currentStates.referenceState?.linksOut,
      "referenceNumber",
      resolvedMap,
    ),
    modulesIn: applyResolvedLabelsToItems(
      currentStates.referenceState?.modulesIn,
      "referenceNumber",
      resolvedMap,
    ),
    modulesOut: applyResolvedLabelsToItems(
      currentStates.referenceState?.modulesOut,
      "referenceNumber",
      resolvedMap,
    ),
  };

  const nextOtherEditionsState = {
    ...DEFAULT_OTHER_EDITIONS_STATE,
    ...(currentStates.otherEditionsState ?? {}),
    documents: applyResolvedLabelsToItems(
      currentStates.otherEditionsState?.documents,
      "refNo",
      resolvedMap,
    ),
  };

  const referenceStateChanged = !sameStateValue(
    currentStates.referenceState,
    nextReferenceState,
  );
  const otherEditionsStateChanged = !sameStateValue(
    currentStates.otherEditionsState,
    nextOtherEditionsState,
  );

  if (!referenceStateChanged && !otherEditionsStateChanged) {
    return currentStates;
  }

  return {
    ...currentStates,
    referenceState: nextReferenceState,
    otherEditionsState: nextOtherEditionsState,
  };
};

export const buildEditStatesFromSummary = (summary, pageMode = "", fallbackSource = null) => ({
  overviewState: {
    ...DEFAULT_OVERVIEW_STATE,
    refNo: summary?.referenceNumber ?? "",
    title: summary?.title ?? "",
    standardOwnerId: resolveStandardOwnerId(summary),
    standardOwnerLabel: resolveStandardOwnerLabel(summary, fallbackSource),
    responsibleTeam:
      summary?.responsibleTeamId != null ? String(summary.responsibleTeamId) : "",
    functionalOwner: summary?.functionalOwner ?? "",
    shortDescription: summary?.description ?? "",
    category: summary?.categoryId != null ? String(summary.categoryId) : "",
    documentType:
      summary?.documentTypeId != null ? String(summary.documentTypeId) : "",
    // Copy-issue starts from the copied issue value so the next issue can be amended easily.
    issueNumber: summary?.issue ?? "",
    level: summary?.normalLevelId != null ? String(summary.normalLevelId) : "",
    status: summary?.statusId ?? "",
    nextReleaseStatus: summary?.nextReleaseStatus ?? "",
    publicationDate: formatDateInput(summary?.publicationDate),
    complianceDate: formatDateInput(summary?.complianceDate),
    reviewDate: formatDateInput(summary?.reviewDate),
    withdrawalDate: formatDateInput(summary?.withdrawnDate),
    reasonForChange: summary?.whatsNew ?? "",
    sensitivity:
      summary?.acl?.sensitivityId != null ? String(summary.acl.sensitivityId) : "",
    visibility: buildVisibilityFromAclRole(summary?.acl?.role),
    accessGroup: buildAccessGroupsFromSummary(summary),
    tags: buildTagStateFromSummary(summary),
  },
  otherEditionsState: buildOtherEditionsState(summary),
  pdssState: {
    ...DEFAULT_PDSS_STATE,
    pdssApplicable:
      summary?.pdss?.pdssApplicable != null ? String(summary.pdss.pdssApplicable) : "",
    pdssAllProjects:
      summary?.pdss?.pdssAllProjects != null ? String(summary.pdss.pdssAllProjects) : "",
    level1: summary?.pdss?.level1 != null ? String(summary.pdss.level1) : "",
    level2: summary?.pdss?.level2 != null ? String(summary.pdss.level2) : "",
    level3: summary?.pdss?.level3 != null ? String(summary.pdss.level3) : "",
    level4: summary?.pdss?.level4 != null ? String(summary.pdss.level4) : "",
  },
  referenceState: {
    ...DEFAULT_REFERENCE_STATE,
    refNo: summary?.referenceNumber ?? "",
    linksIn: buildUniqueReferenceLinks(summary?.linksIn ?? []),
    linksOut: buildUniqueReferenceLinks(summary?.linksOut ?? []),
    modulesIn: buildUniqueReferenceModules(summary?.modulesIn ?? []),
    modulesOut: buildUniqueReferenceModules(summary?.modulesOut ?? []),
  },
});

export const buildUpdatePayload = ({
  pageMode,
  originalSummary,
  overviewState,
  otherEditionsState,
  pdssState,
  referenceState,
}) => {
  if (!originalSummary) return {};

  const payload = {};

  const currentReferenceNumber = normalizeRequiredString(overviewState.refNo);
  const originalReferenceNumber = normalizeRequiredString(originalSummary.referenceNumber);
  if (currentReferenceNumber !== originalReferenceNumber) {
    payload.referenceNumber = currentReferenceNumber;
  }

  const currentTitle = normalizeRequiredString(overviewState.title);
  const originalTitle = normalizeRequiredString(originalSummary.title);
  if (currentTitle !== originalTitle) {
    payload.title = currentTitle;
  }

  const scalarFields = [
    ["documentTypeId", toNumberOrNull(overviewState.documentType), toNumberOrNull(originalSummary.documentTypeId)],
    ["categoryId", toNumberOrNull(overviewState.category), toNumberOrNull(originalSummary.categoryId)],
    ["responsibleTeamId", toNumberOrNull(overviewState.responsibleTeam), toNumberOrNull(originalSummary.responsibleTeamId)],
    ["normalLevelId", toNumberOrNull(overviewState.level), toNumberOrNull(originalSummary.normalLevelId)],
    ["standardOwnerId", toNumberOrNull(overviewState.standardOwnerId), toNumberOrNull(originalSummary.standardOwnerId)],
  ];

  scalarFields.forEach(([key, currentValue, originalValue]) => {
    if (currentValue !== originalValue) payload[key] = currentValue;
  });

  const currentStatusId = normalizeRequiredString(overviewState.status);
  const originalStatusId = normalizeRequiredString(originalSummary.statusId);
  if (currentStatusId !== originalStatusId) {
    payload.statusId = currentStatusId;
  }

  const nullableStringFields = [
    ["nextReleaseStatus", normalizeNullableString(overviewState.nextReleaseStatus), normalizeNullableString(originalSummary.nextReleaseStatus)],
    ["issue", normalizeNullableString(overviewState.issueNumber), normalizeNullableString(originalSummary.issue)],
    ["description", normalizeRichText(overviewState.shortDescription), normalizeRichText(originalSummary.description)],
    ["functionalOwner", normalizeNullableString(overviewState.functionalOwner), normalizeNullableString(originalSummary.functionalOwner)],
    ["whatsNew", normalizeNullableString(overviewState.reasonForChange), normalizeNullableString(originalSummary.whatsNew)],
  ];

  nullableStringFields.forEach(([key, currentValue, originalValue]) => {
    if (currentValue !== originalValue) payload[key] = currentValue;
  });

  const nullableDateFields = [
    ["publicationDate", normalizeNullableString(overviewState.publicationDate), normalizeNullableString(formatDateInput(originalSummary.publicationDate))],
    ["complianceDate", normalizeNullableString(overviewState.complianceDate), normalizeNullableString(formatDateInput(originalSummary.complianceDate))],
    ["reviewDate", normalizeNullableString(overviewState.reviewDate), normalizeNullableString(formatDateInput(originalSummary.reviewDate))],
    ["withdrawnDate", normalizeNullableString(overviewState.withdrawalDate), normalizeNullableString(formatDateInput(originalSummary.withdrawnDate))],
  ];

  nullableDateFields.forEach(([key, currentValue, originalValue]) => {
    if (currentValue !== originalValue) payload[key] = currentValue;
  });

  const currentTagIds = normalizeIdArray((overviewState.tags ?? []).map((tag) => tag?.id));
  const originalTagIds = buildTagIdsFromSummary(originalSummary);
  if (!sameArrayValue(currentTagIds, originalTagIds)) {
    payload.tagIds = currentTagIds;
  }

  const currentSupersededIds = buildSupersededIdsFromDocuments(otherEditionsState.documents);
  const originalSupersededIds = normalizeIdArray(originalSummary.supersededStandardIds);
  if (!sameArrayValue(currentSupersededIds, originalSupersededIds)) {
    payload.supersededStandardIds = currentSupersededIds;
  }

  const currentLinksIn = normalizeLinkArray(referenceState.linksIn);
  const originalLinksIn = normalizeLinkArray(originalSummary.linksIn);
  if (!sameArrayValue(currentLinksIn, originalLinksIn)) {
    payload.linksIn = currentLinksIn;
  }

  const currentLinksOut = normalizeLinkArray(referenceState.linksOut);
  const originalLinksOut = normalizeLinkArray(originalSummary.linksOut);
  if (!sameArrayValue(currentLinksOut, originalLinksOut)) {
    payload.linksOut = currentLinksOut;
  }

  const currentModulesIn = normalizeIdArray((referenceState.modulesIn ?? []).map((item) => item?.standardId));
  const originalModulesIn = normalizeIdArray((originalSummary.modulesIn ?? []).map((item) => item?.standardId ?? item));
  if (!sameArrayValue(currentModulesIn, originalModulesIn)) {
    payload.modulesIn = currentModulesIn;
  }

  const currentModulesOut = normalizeIdArray((referenceState.modulesOut ?? []).map((item) => item?.standardId));
  const originalModulesOut = normalizeIdArray((originalSummary.modulesOut ?? []).map((item) => item?.standardId ?? item));
  if (!sameArrayValue(currentModulesOut, originalModulesOut)) {
    payload.modulesOut = currentModulesOut;
  }

  const currentPdss = buildCurrentPdss(pdssState);
  const originalPdss = buildOriginalPdss(originalSummary);
  const pdssPatch = {};
  Object.entries(currentPdss).forEach(([key, value]) => {
    if (value !== originalPdss[key]) pdssPatch[key] = value;
  });
  if (Object.keys(pdssPatch).length) {
    payload.pdss = pdssPatch;
  }

  const currentRole = buildCurrentAclRole(overviewState.visibility);
  const originalRole = buildOriginalAclRole(originalSummary);
  const currentSensitivityId = toNumberOrNull(overviewState.sensitivity);
  const originalSensitivityId = toNumberOrNull(originalSummary?.acl?.sensitivityId);
  const aclPatch = {};
  if (!sameStateValue(currentRole, originalRole) && currentRole != null) {
    aclPatch.role = currentRole;
  }
  if (currentSensitivityId !== originalSensitivityId) {
    aclPatch.sensitivityId = currentSensitivityId;
  }
  const currentAccessGroupIds = normalizeAccessGroupIds(overviewState.accessGroup);
  const originalAccessGroupIds = normalizeAccessGroupIds(
    originalSummary?.acl?.groups ?? originalSummary?.acl?.groupIds ?? [],
  );
  if (!sameArrayValue(currentAccessGroupIds, originalAccessGroupIds)) {
    aclPatch.groups = currentAccessGroupIds;
  }
  if (Object.keys(aclPatch).length) {
    payload.acl = aclPatch;
  }

  return payload;
};
